import {
  buildMediaFileName,
  buildPromptsCsv,
  getMediaDownloadUrl,
  type MediaItem,
  type MediaManifestRow,
  resolveMediaExtension,
} from './mediaItems';
import { isR2PublicUrl } from '../r2PublicUrl';

// Downloads media as a ZIP built in the browser. Files come straight from our
// CDN (CORS allows the app origin), falling back to the same-origin proxy
// (/api/download-image) for hosts that refuse the request — localhost, old
// storage hosts. The archive either streams to a file the user picked
// (File System Access API, any size) or is assembled as a Blob and split into
// parts so a huge export can't exhaust memory in Safari or Firefox.

export interface DownloadProgress {
  filesDone: number;
  filesTotal: number;
  failed: number;
  bytes: number;
  part: number;
}

export interface SaveFileHandle {
  createWritable: () => Promise<WritableStream<Uint8Array>>;
  remove?: () => Promise<void>;
}

export interface MediaDownloadOptions {
  items: MediaItem[];
  /** Archive name without extension, e.g. `easy-peasy-images-2026-10-05`. */
  baseName: string;
  /** From pickZipSaveTarget: stream the archive into this file. */
  fileHandle?: SaveFileHandle | null;
  signal?: AbortSignal;
  onProgress?: (progress: DownloadProgress) => void;
  concurrency?: number;
  /** Blob mode: start a new archive once a part reaches this size. */
  maxPartBytes?: number;
  /**
   * Stream mode: stop adding files once the archive reaches this size and
   * report the rest as `remaining`, so one file never needs ZIP64.
   */
  maxArchiveBytes?: number;
  fetchImpl?: typeof fetch;
  saveBlob?: (blob: Blob, fileName: string) => void;
  /** Same-origin fallback URL for a file; null disables the fallback. */
  proxyUrl?: ((url: string, fileName: string) => string) | null;
  retryDelayMs?: number;
}

export interface MediaDownloadResult {
  mode: 'single' | 'blob' | 'stream';
  files: number;
  /** Items whose file is in the download. */
  downloaded: MediaItem[];
  failed: MediaItem[];
  /** Items not attempted because the archive hit its size cap. */
  remaining: MediaItem[];
  bytes: number;
  parts: number;
}

/** Above this estimate the archive streams to disk when the browser can. */
export const STREAM_TO_DISK_THRESHOLD_BYTES = 300 * 1024 * 1024;
const DEFAULT_MAX_PART_BYTES = 450 * 1024 * 1024;
const DEFAULT_MAX_ARCHIVE_BYTES = 3.5 * 1024 * 1024 * 1024;
const DEFAULT_CONCURRENCY = 4;
const UNKNOWN_FILE_BYTES = 2 * 1024 * 1024;
const PROGRESS_INTERVAL_MS = 120;

// Whether a host answers this page's CORS requests. The CDN only allows our
// production origins (plus the usual local dev port), so on previews or other
// ports the first request to a host decides for the rest of the session: one
// refused request instead of one console error per file.
const directSupport = new Map<string, boolean | Promise<boolean>>();

/** The CDN says the file is gone (401/403/404/410), or no route could fetch it. */
export class MissingFileError extends Error {}

const isAbortError = (error: unknown) =>
  (error as { name?: string })?.name === 'AbortError';

const abortError = () =>
  typeof DOMException === 'function'
    ? new DOMException('The download was cancelled.', 'AbortError')
    : Object.assign(new Error('The download was cancelled.'), {
        name: 'AbortError',
      });

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(abortError());
      },
      { once: true },
    );
  });

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

export const defaultProxyUrl = (url: string, fileName: string) =>
  `/api/download-image?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(fileName)}`;

export const saveBlobToDisk = (blob: Blob, fileName: string) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking right away can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
};

export const canStreamToDisk = (): boolean =>
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  window.self === window.top &&
  typeof (window as unknown as { showSaveFilePicker?: unknown })
    .showSaveFilePicker === 'function';

/**
 * Asks where to save the archive. Call it first thing in a click handler: the
 * picker needs the click's user activation. Resolves null when the user
 * cancels; throws when the picker can't open (the caller falls back to a Blob).
 */
export const pickZipSaveTarget = async (
  suggestedName: string,
): Promise<SaveFileHandle | null> => {
  const picker = (
    window as unknown as {
      showSaveFilePicker: (options: unknown) => Promise<SaveFileHandle>;
    }
  ).showSaveFilePicker;
  try {
    return await picker({
      suggestedName,
      types: [
        {
          description: 'ZIP archive',
          accept: { 'application/zip': ['.zip'] },
        },
      ],
    });
  } catch (error) {
    if (isAbortError(error)) return null;
    throw error;
  }
};

interface FetchContext {
  fetchImpl: typeof fetch;
  signal: AbortSignal;
  proxyUrl: ((url: string, fileName: string) => string) | null;
  retryDelayMs: number;
}

const fetchDirect = async (url: string, ctx: FetchContext) => {
  const host = hostOf(url);
  const known = directSupport.get(host);
  if (known === false) return null;
  // Another file is probing this host right now: wait for its answer.
  if (known instanceof Promise && !(await known)) return null;

  let settleProbe: ((supported: boolean) => void) | null = null;
  if (known === undefined) {
    directSupport.set(
      host,
      new Promise<boolean>((resolve) => {
        settleProbe = resolve;
      }),
    );
  }
  const settle = (supported: boolean) => {
    if (!settleProbe) return;
    directSupport.set(host, supported);
    settleProbe(supported);
    settleProbe = null;
  };

  try {
    for (let attempt = 0; attempt < 2; attempt++) {
      let response: Response;
      try {
        // no-store: an <img> may have cached this file without CORS headers,
        // and reusing that entry would fail the CORS check.
        response = await ctx.fetchImpl(url, {
          mode: 'cors',
          cache: 'no-store',
          credentials: 'omit',
          signal: ctx.signal,
        });
      } catch (error) {
        if (isAbortError(error) || ctx.signal.aborted) throw abortError();
        // CORS refusal or a dropped connection: the proxy takes over.
        settle(false);
        return null;
      }
      // Any readable response proves the host allows this origin.
      settle(true);
      if (response.ok) return response;
      if ([401, 403, 404, 410].includes(response.status)) {
        throw new MissingFileError(`File unavailable (${response.status})`);
      }
      // 429 / 5xx: one more try before handing over to the proxy.
      if (attempt === 0) await wait(ctx.retryDelayMs, ctx.signal);
    }
    return null;
  } finally {
    // An abort mid-probe must not leave other files waiting forever.
    if (settleProbe) {
      directSupport.delete(host);
      (settleProbe as (supported: boolean) => void)(false);
    }
  }
};

const fetchViaProxy = async (
  url: string,
  fileName: string,
  ctx: FetchContext,
) => {
  if (!ctx.proxyUrl) return null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await ctx.fetchImpl(ctx.proxyUrl(url, fileName), {
        signal: ctx.signal,
      });
      if (response.ok) return response;
      // The proxy refuses hosts outside our storage and reports a missing
      // file as a 502 after its own fetch; neither improves with retries.
      if (response.status < 500 || attempt === 2) return null;
    } catch (error) {
      if (isAbortError(error) || ctx.signal.aborted) throw abortError();
      if (attempt === 2) return null;
    }
    await wait(ctx.retryDelayMs * (attempt + 1), ctx.signal);
  }
  return null;
};

const fetchMediaFile = async (
  url: string,
  fileName: string,
  ctx: FetchContext,
): Promise<Response> => {
  const direct = await fetchDirect(url, ctx);
  if (direct) return direct;
  const proxied = await fetchViaProxy(url, fileName, ctx);
  if (proxied) return proxied;
  throw new MissingFileError('File could not be downloaded');
};

interface FetchedFile {
  item: MediaItem;
  name: string;
  size: number | null;
  lastModified: Date;
  stream: ReadableStream<Uint8Array>;
}

const contentLength = (response: Response) => {
  const value = Number(response.headers.get('content-length'));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const lastModifiedOf = (item: MediaItem) => {
  const date = item.created_at ? new Date(item.created_at) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

/** Yields fetched files in the given order while keeping a few requests in flight. */
async function* fetchInOrder(
  items: MediaItem[],
  ctx: FetchContext & {
    concurrency: number;
    usedNames: Set<string>;
    onBytes: (count: number) => void;
    onFileDone: () => void;
    onFailed: (item: MediaItem) => void;
  },
): AsyncGenerator<FetchedFile> {
  const load = async (item: MediaItem): Promise<FetchedFile | null> => {
    const url = getMediaDownloadUrl(item);
    if (!url) {
      ctx.onFailed(item);
      return null;
    }
    try {
      const provisionalName = buildMediaFileName(
        item,
        resolveMediaExtension(item, url),
      );
      const response = await fetchMediaFile(url, provisionalName, ctx);
      if (!response.body) throw new MissingFileError('Empty response');
      const name = buildMediaFileName(
        item,
        resolveMediaExtension(item, url, response.headers.get('content-type')),
        ctx.usedNames,
      );
      const counter = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          ctx.onBytes(chunk.byteLength);
          controller.enqueue(chunk);
        },
        flush() {
          ctx.onFileDone();
        },
      });
      return {
        item,
        name,
        size: contentLength(response),
        lastModified: lastModifiedOf(item),
        stream: response.body.pipeThrough(counter),
      };
    } catch (error) {
      if (isAbortError(error) || ctx.signal.aborted) throw abortError();
      ctx.onFailed(item);
      return null;
    }
  };

  const pending: Promise<FetchedFile | null>[] = [];
  let next = 0;
  const launch = () => {
    if (next >= items.length) return;
    const task = load(items[next++]);
    // Rejections surface when the task is awaited below; this only keeps a
    // cancelled export from logging unhandled rejections.
    task.catch(() => undefined);
    pending.push(task);
  };
  for (let i = 0; i < ctx.concurrency; i++) launch();

  while (pending.length) {
    const file = await (pending.shift() as Promise<FetchedFile | null>);
    launch();
    if (file) yield file;
  }
}

const manifestEntries = (rows: MediaManifestRow[], failed: MediaItem[]) => {
  const now = new Date();
  const entries: { name: string; lastModified: Date; input: string }[] = [];
  if (rows.length) {
    entries.push({
      name: 'prompts.csv',
      lastModified: now,
      input: buildPromptsCsv(rows),
    });
  }
  if (failed.length) {
    const lines = failed.map(
      (item) =>
        `${item.id ?? ''}\t${item.created_at ?? ''}\t${getMediaDownloadUrl(item) ?? '(no file)'}`,
    );
    entries.push({
      name: 'missing-files.txt',
      lastModified: now,
      input: `These items could not be downloaded:\r\n\r\nid\tcreated_at\turl\r\n${lines.join('\r\n')}\r\n`,
    });
  }
  return entries;
};

export const downloadMedia = async (
  options: MediaDownloadOptions,
): Promise<MediaDownloadResult> => {
  const {
    items,
    baseName,
    fileHandle,
    onProgress,
    concurrency = DEFAULT_CONCURRENCY,
    maxPartBytes = DEFAULT_MAX_PART_BYTES,
    maxArchiveBytes = DEFAULT_MAX_ARCHIVE_BYTES,
    fetchImpl = (input, init) => fetch(input, init),
    saveBlob = saveBlobToDisk,
    proxyUrl = defaultProxyUrl,
    retryDelayMs = 800,
  } = options;

  // An internal controller so an aborted or failed export also stops the
  // requests still in flight.
  const controller = new AbortController();
  const onOuterAbort = () => controller.abort();
  if (options.signal?.aborted) throw abortError();
  options.signal?.addEventListener('abort', onOuterAbort, { once: true });

  const failed: MediaItem[] = [];
  const progress: DownloadProgress = {
    filesDone: 0,
    filesTotal: items.length,
    failed: 0,
    bytes: 0,
    part: 1,
  };
  let lastEmit = 0;
  const emit = (force = false) => {
    const now = Date.now();
    if (!force && now - lastEmit < PROGRESS_INTERVAL_MS) return;
    lastEmit = now;
    onProgress?.({ ...progress });
  };
  emit(true);

  const ctx: FetchContext = {
    fetchImpl,
    signal: controller.signal,
    proxyUrl,
    retryDelayMs,
  };

  const rows: MediaManifestRow[] = [];
  const result = (
    mode: MediaDownloadResult['mode'],
    parts: number,
  ): MediaDownloadResult => {
    const downloaded = rows.map((row) => row.item);
    const settled = new Set<MediaItem>([...downloaded, ...failed]);
    return {
      mode,
      files: rows.length,
      downloaded,
      failed,
      remaining: items.filter((item) => !settled.has(item)),
      bytes: progress.bytes,
      parts,
    };
  };

  try {
    // One file: save it as-is, no archive (unless a ZIP target was picked).
    if (items.length === 1 && !fileHandle) {
      const [item] = items;
      const url = getMediaDownloadUrl(item);
      if (!url) {
        failed.push(item);
        return result('single', 0);
      }
      try {
        const response = await fetchMediaFile(
          url,
          buildMediaFileName(item, resolveMediaExtension(item, url)),
          ctx,
        );
        const blob = await response.blob();
        const name = buildMediaFileName(
          item,
          resolveMediaExtension(
            item,
            url,
            response.headers.get('content-type'),
          ),
        );
        saveBlob(blob, name);
        rows.push({ file: name, item });
        progress.filesDone = 1;
        progress.bytes = blob.size;
        emit(true);
        return result('single', 1);
      } catch (error) {
        if (isAbortError(error) || controller.signal.aborted)
          throw abortError();
        failed.push(item);
        return result('single', 0);
      }
    }

    const { downloadZip, makeZip } = await import('client-zip');
    const source = fetchInOrder(items, {
      ...ctx,
      concurrency,
      usedNames: new Set(['prompts.csv', 'missing-files.txt']),
      onBytes: (count) => {
        progress.bytes += count;
        emit();
      },
      onFileDone: () => {
        progress.filesDone += 1;
        emit(true);
      },
      onFailed: (item) => {
        failed.push(item);
        progress.failed = failed.length;
        emit(true);
      },
    })[Symbol.asyncIterator]();

    if (fileHandle) {
      const entries = async function* () {
        for (;;) {
          // Size cap: finish this archive; the rest is reported as remaining.
          if (rows.length && progress.bytes >= maxArchiveBytes) break;
          const next = await source.next();
          if (next.done) break;
          const file = next.value;
          rows.push({ file: file.name, item: file.item });
          yield {
            name: file.name,
            lastModified: file.lastModified,
            input: file.stream,
          };
        }
        yield* manifestEntries(rows, failed);
      };
      const writable = await fileHandle.createWritable();
      try {
        await makeZip(entries()).pipeTo(writable, {
          signal: controller.signal,
        });
      } catch (error) {
        // pipeTo aborts the writable, so nothing half-written is committed;
        // the empty file the picker created is removed where supported.
        await fileHandle.remove?.().catch(() => undefined);
        throw controller.signal.aborted ? abortError() : error;
      }
      if (!rows.length) await fileHandle.remove?.().catch(() => undefined);
      emit(true);
      return result('stream', rows.length ? 1 : 0);
    }

    let carry: FetchedFile | null = null;
    let exhausted = false;
    let parts = 0;
    while (!exhausted) {
      progress.part = parts + 1;
      let partBytes = 0;
      let partFiles = 0;
      const entries = async function* () {
        for (;;) {
          let file = carry;
          carry = null;
          if (!file) {
            const next = await source.next();
            if (next.done) {
              exhausted = true;
              break;
            }
            file = next.value;
          }
          const size = file.size ?? UNKNOWN_FILE_BYTES;
          if (partFiles > 0 && partBytes + size > maxPartBytes) {
            carry = file;
            return;
          }
          partBytes += size;
          partFiles += 1;
          rows.push({ file: file.name, item: file.item });
          yield {
            name: file.name,
            lastModified: file.lastModified,
            input: file.stream,
          };
        }
        // The last archive carries the manifest for the whole export.
        yield* manifestEntries(rows, failed);
      };
      const blob = await downloadZip(entries()).blob();
      if (controller.signal.aborted) throw abortError();
      if (!partFiles) break;
      parts += 1;
      const isOnlyPart = parts === 1 && exhausted;
      saveBlob(
        blob,
        isOnlyPart ? `${baseName}.zip` : `${baseName}-part-${parts}.zip`,
      );
    }
    emit(true);
    return result('blob', parts);
  } finally {
    options.signal?.removeEventListener('abort', onOuterAbort);
    controller.abort();
  }
};

/** Lets the browser download a file through the same-origin proxy (native download, no memory). */
export const downloadViaProxy = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = defaultProxyUrl(url, fileName);
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Saves one of our R2 files through a short-lived signed link that carries the
 * attachment header (/api/media-download-link): the browser streams it to disk
 * straight from R2, any size, no CORS involved. Resolves false when no link
 * could be made (not our file, signed out, a server error).
 */
export const downloadViaSignedLink = async (
  url: string,
  fileName: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<boolean> => {
  if (!isR2PublicUrl(url)) return false;
  try {
    const response = await fetchImpl('/api/media-download-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, filename: fileName }),
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (typeof data?.url !== 'string') return false;
    const link = document.createElement('a');
    link.href = data.url;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch {
    return false;
  }
};

/** Files the page can't hold or fetch: a signed R2 link, else the proxy. */
export const downloadWithoutMemory = async (url: string, fileName: string) => {
  if (await downloadViaSignedLink(url, fileName)) return;
  downloadViaProxy(url, fileName);
};

export interface SingleFileDownloadOptions {
  url: string;
  fileName: string;
  signal?: AbortSignal;
  /** Bytes received so far; `total` is null when the CDN sends no length. */
  onProgress?: (received: number, total: number | null) => void;
  fetchImpl?: typeof fetch;
  saveBlob?: (blob: Blob, fileName: string) => void;
  /** Hands the file to the browser's own download (no memory needed). */
  proxyDownload?: (url: string, fileName: string) => void | Promise<void>;
  retryDelayMs?: number;
  /** Larger files go through the proxy instead of being held in memory. */
  maxMemoryBytes?: number;
}

/**
 * Saves one file under a readable name, fetched straight from the CDN, which
 * costs nothing (R2 has no egress fees) where the old Download buttons
 * streamed every file through /api/download-image on Vercel. When the CDN
 * refuses this origin (preview deployments, other dev ports), or the file is
 * too big to hold in memory (a long 4K video), the browser downloads it itself
 * through a signed R2 link, falling back to that proxy for other hosts.
 * Rejects with MissingFileError when the file is gone.
 */
export const downloadSingleFile = async ({
  url,
  fileName,
  signal,
  onProgress,
  fetchImpl = (input, init) => fetch(input, init),
  saveBlob = saveBlobToDisk,
  proxyDownload = downloadWithoutMemory,
  retryDelayMs = 800,
  maxMemoryBytes = DEFAULT_MAX_PART_BYTES,
}: SingleFileDownloadOptions): Promise<'direct' | 'proxy'> => {
  const response = await fetchDirect(url, {
    fetchImpl,
    signal: signal || new AbortController().signal,
    proxyUrl: null,
    retryDelayMs,
  });
  if (!response) {
    await proxyDownload(url, fileName);
    return 'proxy';
  }

  const total = contentLength(response);
  if (total !== null && total > maxMemoryBytes) {
    response.body?.cancel().catch(() => undefined);
    await proxyDownload(url, fileName);
    return 'proxy';
  }
  const type = response.headers.get('content-type') || undefined;
  if (!response.body) {
    saveBlob(await response.blob(), fileName);
    return 'direct';
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    onProgress?.(received, total);
  }
  saveBlob(new Blob(chunks as BlobPart[], { type }), fileName);
  return 'direct';
};

/** `easy-peasy-images-2026-10-05` style archive names (local date). */
export const archiveBaseName = (prefix: string, date = new Date()) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${prefix}-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};
